import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AGING_COLORS, USDollar, USDollarShort, toNumber } from "./utils";

const BarTooltip = ({ active, payload }) => {
  if (!(active && payload && payload.length)) return null;
  const entry = payload[0].payload;
  return (
    <div className="bg-black text-white rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold mb-1">{entry.label}</p>
      <p>{USDollar.format(entry.sum)}</p>
      <p className="mb-2">{entry.share.toFixed(2)}% of total</p>
      <p className="text-[11px] text-gray-300 border-t border-gray-600 pt-2">
        Click for details
      </p>
    </div>
  );
};

const RADIAN = Math.PI / 180;

const renderSliceLabel = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  midAngle,
  percent,
  payload,
}) => {
  const share =
    payload?.share != null ? toNumber(payload.share) : toNumber(percent) * 100;
  if (share < 7) return null;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-[11px] font-semibold"
      style={{ pointerEvents: "none" }}
    >
      {share.toFixed(1)}%
    </text>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!(active && payload && payload.length)) return null;
  const entry = payload[0].payload;
  return (
    <div className="bg-black text-white rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold mb-1">{entry.label}</p>
      <p>{entry.share.toFixed(2)}%</p>
      <p>{USDollar.format(entry.sum)}</p>
    </div>
  );
};

const colorOf = (chartData, entry) => {
  const index = chartData.findIndex((item) => item.key === entry.key);
  return AGING_COLORS[(index < 0 ? 0 : index) % AGING_COLORS.length];
};

export default function AgingCharts({ period, onDrill }) {
  const chartData = (period?.aging || []).map((range) => ({
    key: range.key,
    label: range.label,
    sum: toNumber(range.sum),
    share: toNumber(range.percent),
  }));
  const pieData = chartData.filter((entry) => entry.sum > 0);

  const handleDrill = (entry) => {
    if (!entry || entry.sum <= 0) return;
    onDrill({
      reference_date: period.reference_date,
      aging_key: entry.key,
      title: entry.label,
    });
  };

  return (
    <div className="w-full flex flex-col xl:flex-row gap-4">
      <div className="flex-1 border rounded-lg p-3 px-5">
        <h2 className="text-sm font-bold">Aging by Value</h2>
        <p className="text-xs font-extralight text-gray-500 mb-4">
          Open balance per aging range
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 20, right: 10, left: 10 }}>
            <CartesianGrid vertical={false} strokeOpacity={0.15} />
            <XAxis dataKey="label" fontSize={11} interval={0} height={40} />
            <YAxis
              width={70}
              fontSize={11}
              tickFormatter={(value) => USDollarShort.format(value)}
            />
            <Tooltip
              content={<BarTooltip />}
              cursor={{ fill: "rgba(136, 132, 216, 0.1)" }}
            />
            <Bar
              dataKey="sum"
              onClick={(bar) => handleDrill(bar?.payload)}
              cursor="pointer"
            >
              <LabelList
                dataKey="sum"
                position="top"
                fontSize={11}
                formatter={(value) =>
                  value ? USDollarShort.format(value) : ""
                }
              />
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.key}
                  fill={AGING_COLORS[index % AGING_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex-1 border rounded-lg p-3 px-5">
        <h2 className="text-sm font-bold">Aging by %</h2>
        <p className="text-xs font-extralight text-gray-500 mb-4">
          Share of the total open balance
        </p>
        {pieData.length === 0 ? (
          <div
            className="flex items-center justify-center text-xs text-gray-500"
            style={{ height: 260 }}
          >
            No overdue balance for this period
          </div>
        ) : (
          <div className="flex flex-row flex-wrap items-center gap-4">
            <div className="shrink-0" style={{ width: 260, height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="share"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={1}
                    fill="#8884d8"
                    labelLine={false}
                    label={renderSliceLabel}
                    onClick={(slice) => handleDrill(slice?.payload)}
                    isAnimationActive={false}
                  >
                    {pieData.map((entry) => (
                      <Cell
                        key={entry.key}
                        fill={colorOf(chartData, entry)}
                        style={{ cursor: "pointer" }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <ul
              className="flex-1 flex flex-col gap-1 text-xs"
              style={{ minWidth: 240, maxWidth: 420 }}
            >
              {chartData.map((entry) => (
                <li
                  key={entry.key}
                  onClick={() => handleDrill(entry)}
                  className={`flex flex-row items-center gap-2 p-1 rounded ${
                    entry.sum > 0
                      ? "cursor-pointer hover:bg-green-50"
                      : "text-gray-400"
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ backgroundColor: colorOf(chartData, entry) }}
                  />
                  <span className="flex-1 truncate">{entry.label}</span>
                  <span className="font-semibold w-14 text-right">
                    {entry.share.toFixed(1)}%
                  </span>
                  <span className="text-gray-500 w-24 text-right">
                    {USDollar.format(entry.sum)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
